(() => {
  'use strict';

  const VERSION = '2.5.0';
  const normalise = (value = '') => String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const unique = (items) => [...new Set(items.map((item) => String(item || '').trim()).filter(Boolean))];
  const words = (value) => normalise(value).split(' ').filter(Boolean);

  const spellingWords = [
    'commodore','calais','berlina','statesman','caprice','headlight','headlights','taillight','taillights',
    'weatherstrip','weather','stereo','radio','fascia','bonnet','bumper','mirror','aerial','antenna',
    'indicator','instrument','cluster','parcel','shelf','console','carplay','android','bluetooth','strut',
    'rubber','seal','mould','moulding','window','door','boot','grille','badge','emblem','switch'
  ];

  const protectedShortWords = new Set(['vb','vc','vh','vk','vl','vn','vp','vq','vr','vs','vt','vx','vy','vz','ve','vf','ss','sv6']);

  const explicitCorrections = {
    comodore: 'commodore', comadore: 'commodore', commadore: 'commodore', commodor: 'commodore', commodre: 'commodore',
    calias: 'calais', calis: 'calais', calaiss: 'calais', berlna: 'berlina', berlinaa: 'berlina',
    bonet: 'bonnet', miror: 'mirror', mirors: 'mirrors', headlite: 'headlight', headlites: 'headlights',
    taillite: 'tail light', taillites: 'tail lights', weatherstrp: 'weather strip', weatherstip: 'weather strip',
    fasica: 'fascia', facia: 'fascia', ariel: 'aerial', antena: 'antenna', indicater: 'indicator'
  };

  const joinedWords = [
    ['doorbelt', 'door belt'], ['weatherstrip', 'weather strip'], ['taillight', 'tail light'],
    ['headlight', 'headlight'], ['doorhandle', 'door handle'], ['doorseal', 'door seal'],
    ['headunit', 'head unit'], ['carplay', 'carplay'], ['doubledin', 'double din'],
    ['singledin', 'single din'], ['satnav', 'sat nav'], ['dashmat', 'dash mat'],
    ['mudflap', 'mud flap'], ['parcelshelf', 'parcel shelf'], ['pinchweld', 'pinch weld'],
    ['floormat', 'floor mat'], ['seatcover', 'seat cover']
  ];

  const levenshtein = (a, b) => {
    a = normalise(a); b = normalise(b);
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
      let left = i;
      for (let j = 1; j <= b.length; j += 1) {
        const above = prev[j];
        const diagonal = prev[j - 1];
        const current = Math.min(left + 1, above + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
        prev[j - 1] = left;
        left = current;
      }
      prev[b.length] = left;
    }
    return prev[b.length];
  };

  const correctWord = (word) => {
    const lower = word.toLowerCase();
    if (explicitCorrections[lower]) return { word: explicitCorrections[lower], changed: true, confidence: 100 };
    if (protectedShortWords.has(lower) || lower.length < 5 || spellingWords.includes(lower)) return { word, changed: false, confidence: 100 };

    let best = null;
    spellingWords.forEach((candidate) => {
      const distance = levenshtein(lower, candidate);
      const allowed = lower.length >= 9 ? 2 : 1;
      if (distance <= allowed && (!best || distance < best.distance)) best = { candidate, distance };
    });
    if (!best) return { word, changed: false, confidence: 100 };
    return { word: best.candidate, changed: true, confidence: best.distance === 1 ? 96 : 88 };
  };

  const correctSpelling = (raw) => {
    let value = String(raw || '').replace(/\s+/g, ' ').trim();
    joinedWords.forEach(([joined, split]) => {
      value = value.replace(new RegExp(`\\b${joined}\\b`, 'gi'), split);
    });

    let confidence = 100;
    let changed = false;
    const corrected = value.split(/(\s+)/).map((token) => {
      if (/^\s+$/.test(token)) return token;
      const result = correctWord(token);
      if (result.changed) {
        changed = true;
        confidence = Math.min(confidence, result.confidence);
      }
      return result.word;
    }).join('');

    return { value: corrected, changed, confidence };
  };

  const intents = [
    { id:'radio-fascia', label:'Radio Fascia', primary:'radio fascia', phrases:['radio fascia','stereo fascia','head unit fascia','radio surround','stereo surround','plastic around radio','plastic around stereo','thing around radio','thing around stereo','trim around radio','trim around stereo','radio trim','stereo trim','centre dash surround','center dash surround','dash radio surround','radio bezel','stereo bezel','double din fascia','installation fascia'], fallback:['radio surround','dash fascia','centre dash surround'] },
    { id:'weather-strip', label:'Weather Strips', primary:'weather strips', phrases:['weather belt','weather belts','weather strip','weather strips','weather seal','weatherstrip','door weatherstrip','door weather strip','window weatherstrip','window weather strip','body rubber','pinch weld','pinchweld','bailey channel','glass run','glass channel'], fallback:['door weather strips','window seals','bailey channel'] },
    { id:'door-belt', label:'Door Belt / Window Scraper', primary:'door belt', phrases:['door belt','door belts','doorbelt','door belt mould','doorbelt mould','belt mould','belt molding','belt moulding','window scraper','window scrapers','window wipe','outer weatherstrip','outer weather strip','inner weatherstrip','inner weather strip','window belt','door window rubber','rubber along window','rubber at bottom of window','strip along door glass','door glass scraper'], fallback:['door belt mould','belt mould','window scraper'] },
    { id:'radio', label:'Radio / CarPlay', primary:'radio', phrases:['radio','stereo','car stereo','head unit','deck','double din','single din','touch screen','touchscreen','infotainment','apple carplay','car play','carplay','android auto','sat nav','navigation unit','gps unit','bluetooth stereo','bluetooth radio','multimedia unit'], expand:['carplay'], alwaysExpand:true, fallback:['head unit','stereo','double din radio'] },
    { id:'door-seal', label:'Door Seals', primary:'door seal', phrases:['door seal','door seals','door opening seal','door opening rubber','rubber around door','main door rubber'], fallback:['weather strip','door rubber'] },
    { id:'boot-seal', label:'Boot Seal', primary:'boot seal', phrases:['boot rubber','boot seal','trunk seal','trunk rubber','rubber around boot','boot opening rubber','boot weatherstrip'], fallback:['boot rubber','weather strip'] },
    { id:'bonnet-seal', label:'Bonnet Seal', primary:'bonnet seal', phrases:['bonnet rubber','bonnet seal','hood seal','hood rubber','cowl rubber','rear bonnet seal'], fallback:['bonnet rubber','cowl seal'] },
    { id:'headlight', label:'Headlights', primary:'headlights', phrases:['headlight','headlights','head light','head lights','headlamp','headlamps','front light','front lights'], fallback:['head lamps','front lights'] },
    { id:'tail-light', label:'Tail Lights', primary:'tail lights', phrases:['taillight','taillights','tail light','tail lights','tail lamp','tail lamps','rear light','rear lights','back light','back lights'], fallback:['tail lamps','rear lights'] },
    { id:'indicator', label:'Indicators', primary:'indicators', phrases:['indicator','indicators','blinker','blinkers','turn signal','turn signals','flasher light','corner light','corner lights'], fallback:['indicator lamps','corner lights'] },
    { id:'instrument-cluster', label:'Instrument Cluster', primary:'instrument cluster', phrases:['cluster','instrument cluster','speedo','speedometer','gauge cluster','dash cluster','instrument panel','speedo cluster'], fallback:['speedometer','dash cluster'] },
    { id:'centre-console', label:'Centre Console', primary:'centre console', phrases:['centre console','center console','middle console','gear console','shifter console','transmission console'], fallback:['console','shifter surround'] },
    { id:'parcel-shelf', label:'Parcel Shelf', primary:'parcel shelf', phrases:['parcel shelf','rear parcel shelf','rear shelf','speaker shelf','back shelf','parcel tray'], fallback:['rear shelf','speaker shelf'] },
    { id:'door-handle', label:'Door Handles', primary:'door handles', phrases:['door handle','door handles','inside door handle','inner door handle','outside door handle','outer door handle'], fallback:['door handle'] },
    { id:'window-switch', label:'Window Switches', primary:'window switches', phrases:['window switch','window switches','power window switch','electric window switch','window button','window buttons','master window switch'], fallback:['power window switch'] },
    { id:'mirror', label:'Door Mirrors', primary:'door mirror', phrases:['mirror','mirrors','door mirror','door mirrors','wing mirror','wing mirrors','side mirror','side mirrors'], fallback:['side mirror','mirror'] },
    { id:'grille', label:'Grille', primary:'grille', phrases:['grille','grill','front grille','front grill','radiator grille','radiator grill'], fallback:['front grille'] },
    { id:'bumper', label:'Bumper Bar', primary:'bumper', phrases:['bumper','bumper bar','front bumper','rear bumper','bar cover','bumper cover','front bar','rear bar'], fallback:['bumper bar'] },
    { id:'bonnet-strut', label:'Bonnet Struts', primary:'bonnet struts', phrases:['bonnet strut','bonnet struts','hood strut','hood struts','bonnet gas strut','thing that holds bonnet up','holds bonnet up','bonnet lifter'], fallback:['bonnet gas struts','bonnet support'] },
    { id:'boot-strut', label:'Boot Struts', primary:'boot struts', phrases:['boot strut','boot struts','trunk strut','trunk struts','boot gas strut','thing that holds boot up','holds boot up','boot lifter'], fallback:['boot gas struts','boot support'] },
    { id:'mudflap', label:'Mud Flaps', primary:'mud flaps', phrases:['mud flap','mud flaps','splash guard','splash guards','stone guard'], fallback:['splash guards'] },
    { id:'dash-mat', label:'Dash Mats', primary:'dash mat', phrases:['dash mat','dash mats','dashboard mat','dashboard cover','dash cover'], fallback:['dashboard mat','dash cover'] },
    { id:'badge', label:'Badges and Emblems', primary:'badge', phrases:['badge','badges','emblem','emblems','logo badge','car badge','boot badge','guard badge','grille badge'], expand:['emblem'], alwaysExpand:true, fallback:['emblem'] },
    { id:'aerial', label:'Aerial / Antenna', primary:'aerial', phrases:['aerial','antenna','radio aerial','power aerial','electric aerial','mast','aerial mast','antenna mast'], expand:['antenna'], alwaysExpand:true, fallback:['antenna'] },
    { id:'floor-mat', label:'Floor Mats', primary:'floor mats', phrases:['floor mat','floor mats','car mats','carpet mats','rubber mats'], fallback:['car mats'] },
    { id:'seat-cover', label:'Seat Covers', primary:'seat covers', phrases:['seat cover','seat covers','car seat cover','front seat covers','rear seat covers'], fallback:['seat cover'] }
  ];

  const phraseScore = (query, phrase) => {
    const q = normalise(query); const p = normalise(phrase);
    if (!q || !p) return 0;
    if (q === p) return 1000 + p.length;
    if (` ${q} `.includes(` ${p} `)) return 800 + p.length;
    if (q.startsWith(`${p} `) || q.endsWith(` ${p}`)) return 650 + p.length;
    const qWords = new Set(words(q));
    const pWords = words(p);
    if (pWords.length > 1 && pWords.every((word) => qWords.has(word))) return 500 + p.length;
    return 0;
  };

  const findIntent = (query) => {
    let best = null;
    intents.forEach((intent) => intent.phrases.forEach((phrase) => {
      const score = phraseScore(query, phrase);
      if (score && (!best || score > best.score)) best = { intent, hit: phrase, score };
    }));
    return best;
  };

  const vehicleFrom = (query) => query.match(/\b(VB|VC|VH|VK|VL|VN|VP|VQ|VR|VS|VT|VX|VY|VZ|VE|VF)\b/i)?.[1]?.toUpperCase() || '';
  const preserveVehicle = (query, part) => vehicleFrom(query) ? `${vehicleFrom(query)} Commodore ${part}` : part;

  const interpret = (rawQuery) => {
    const raw = String(rawQuery || '').replace(/\s+/g, ' ').trim();
    const spelling = correctSpelling(raw);
    const cleaned = spelling.value;
    const match = findIntent(cleaned);

    if (!match) {
      return {
        original: raw, corrected: cleaned, primary: cleaned, queries: [cleaned], fallbackQueries: [],
        suggestions: [], changed: normalise(cleaned) !== normalise(raw), spellingChanged: spelling.changed,
        spellingConfidence: spelling.confidence, confidence: spelling.changed ? spelling.confidence : 50,
        intentId: null, label: cleaned,
        explanation: spelling.changed ? `Corrected “${raw}” to “${cleaned}”` : null
      };
    }

    const { intent, score } = match;
    let primary = intent.primary;
    if (intent.id === 'radio' && /\b(carplay|apple carplay|car play)\b/i.test(cleaned)) primary = 'carplay';
    if (intent.id === 'radio' && /\bandroid auto\b/i.test(cleaned)) primary = 'android auto';
    primary = preserveVehicle(cleaned, primary);

    const expansions = (intent.expand || []).map((item) => preserveVehicle(cleaned, item));
    const fallbacks = (intent.fallback || []).map((item) => preserveVehicle(cleaned, item));
    const confidence = Math.min(100, score >= 1000 ? 100 : score >= 800 ? 96 : score >= 650 ? 90 : 82, spelling.confidence);
    const changed = normalise(primary) !== normalise(raw);

    return {
      original: raw, corrected: cleaned, primary,
      queries: unique([primary, ...(intent.alwaysExpand ? expansions : [])]),
      fallbackQueries: unique(fallbacks),
      suggestions: unique([primary, ...fallbacks, ...expansions]).slice(0, 6),
      changed, spellingChanged: spelling.changed, spellingConfidence: spelling.confidence,
      confidence, intentId: intent.id, label: intent.label,
      explanation: changed
        ? `${spelling.changed ? `Corrected “${raw}” and interpreted it` : `Interpreted “${raw}”`} as ${intent.label} · ${confidence}% match`
        : `Searching ${intent.label} · ${confidence}% match`
    };
  };

  const suggest = (rawQuery, limit = 6) => interpret(rawQuery).suggestions.slice(0, limit);

  window.FramewerkSearchBrain = Object.freeze({
    version: VERSION, intents, normalise, interpret, suggest, correctSpelling, phraseScore, findIntent
  });
})();
